"use client";
import React, { useEffect, useState } from "react";
import CareerCard from "@/components/career-card";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config/site";
import ApplyCareer from "@/components/apply-career";
import { Background } from "@/components/background";
import { MainNav } from "@/components/main-nav";
import { marketingConfig } from "@/config/marketing";
import Link from "next/link";
import JobSearch from "@/components/job-search";
import CareerModal from "@/components/career-modal";
import { SiteFooter } from "@/components/site-footer";
import { Skeleton } from "@/components/ui/skeleton";

function Page() {
  const [isHeaderTransparent, setIsHeaderTransparent] = useState(true);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsHeaderTransparent(scrollTop === 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const fetchCareers = async () => {
      try {
        const response = await fetch("/api/careers");
        if (!response.ok) {
          setLoading(false);
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setCareers(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetch careers:", error);
      }
    };

    fetchCareers();
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50/30">
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-indigo-100/40 to-violet-100/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-32 h-[450px] w-[450px] rounded-full bg-gradient-to-br from-purple-100/30 to-pink-100/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-gradient-to-br from-blue-100/20 to-cyan-100/10 blur-3xl" />

      {/* Dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #6366f1 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Header */}
      <header
        className={`relative z-40 sticky inset-x-0 top-0 w-full transition-all duration-500 ${
          isHeaderTransparent
            ? "bg-transparent"
            : "bg-white/70 backdrop-blur-xl border-b border-slate-200/60 shadow-sm"
        }`}
      >
        <div className="container flex h-20 items-center justify-between py-6">
          <MainNav items={marketingConfig.mainNav} />
          <nav>
            <Link
              href="/login"
              className={cn(
                buttonVariants({ variant: "secondary", size: "sm" }),
                "rounded-full px-5 border border-slate-200/80 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-200"
              )}
            >
              Login
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero section */}
      <JobSearch />

      {/* Internship CTA Banner */}
      <section className="relative py-12">
        <div className="container mx-auto max-w-4xl px-6">
          <div
            data-aos="fade-up"
            data-aos-duration="800"
            className="relative overflow-hidden rounded-2xl border border-white/60 bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 p-8 md:p-10 shadow-xl shadow-indigo-500/10"
          >
            {/* Decorative circles */}
            <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

            <div className="relative flex flex-col items-center gap-5 text-center md:flex-row md:text-left md:gap-8">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zM12 14v7" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  Looking for an Internship?
                </h3>
                <p className="mt-2 text-sm md:text-base text-white/80 leading-relaxed">
                  We offer hands-on internship programs where you&apos;ll work on real AI products, learn from industry experts, and build your career.
                </p>
              </div>
              <a
                href="https://internship.botbyte.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-indigo-600 shadow-lg shadow-indigo-900/15 transition-all duration-200 hover:bg-indigo-50 hover:shadow-xl hover:shadow-indigo-900/20 active:scale-[0.97]"
              >
                Apply for Internship
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Job listings */}
      {loading ? (
        <div className="container mx-auto max-w-4xl px-6 py-8">
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      ) : (
        <CareerModal careers={careers} />
      )}

      <SiteFooter simpleFooter={false} />
    </div>
  );
}

export default Page;
